import { useState, useCallback, useMemo, useEffect } from "react";
import {
  BlockStack,
  TextField,
  Button,
  Select,
  Text,
  InlineStack,
  Box,
} from "@medentic/polaris-react";
import { CustomModal } from "../CustomModal";
import {
  AdjustIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useQuery } from "@tanstack/react-query";
import { MultilingualText } from "@features/events/types";

interface GenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onKeep: (text: string, targetLanguage: string) => void;
  placementId: string;
  id: string;
  label: string;
  supportedLanguages: { label: string; value: string }[];
  selectedLanguages: string[];
  value: MultilingualText;
}

interface GenerateRequest {
  rewrite: Array<{
    id: string;
    language: string;
    text: string;
    placementId: string;
    tone: string;
    context: string;
    instructions?: string;
  }>;
}

interface GenerateResponse {
  id: string;
  language: string;
  text: string;
  instructions?: string;
  context: string;
  outputText: string;
  tone: string;
  placementId: string;
}

interface LanguageMetadata {
  tone: Tone[];
  placement: Placement[];
}

type Placement = {
  rule: string;
  object: string;
  slug: string;
};

type Tone = {
  description: string;
  slug: string;
  label: string;
};

export default function GenerateModal({
  isOpen,
  onClose,
  onKeep,
  placementId,
  id,
  label,
  supportedLanguages = [],
  selectedLanguages = [],
  value,
}: GenerateModalProps) {
  const { t } = useTranslation();
  const { POST, GET } = useApiRequest();

  // Get saved tone from localStorage or use default
  const getSavedTone = (): string => {
    try {
      const savedTone = localStorage.getItem("generateModal_lastTone");
      return savedTone || "professional";
    } catch (error) {
      console.warn("Failed to read tone from localStorage:", error);
      return "professional";
    }
  };

  // Form state
  const [context, setContext] = useState("");
  const [tone, setTone] = useState(getSavedTone);
  const [targetLanguage, setTargetLanguage] = useState("bg");
  const [specificInstructions, setSpecificInstructions] = useState("");

  // Save tone to localStorage whenever it changes
  const handleToneChange = useCallback((newTone: string) => {
    setTone(newTone);
    try {
      localStorage.setItem("generateModal_lastTone", newTone);
    } catch (error) {
      console.warn("Failed to save tone to localStorage:", error);
    }
  }, []);

  useEffect(() => {
    if (selectedLanguages.length === 1) {
      setContext(value[selectedLanguages[0]] || "");
      setTargetLanguage(selectedLanguages[0]);
    } else {
      const ifAllLocalesFilled = selectedLanguages.every((l) => {
        const localeValue = value[l];
        return localeValue !== undefined && localeValue !== "";
      });

      if (!ifAllLocalesFilled) {
        const targetLanguage = selectedLanguages.reduce((acc, curr) => {
          if (value[curr] !== "") {
            return curr;
          }
          return acc;
        }, "");
        setContext(value[targetLanguage] || "");
        setTargetLanguage(targetLanguage || selectedLanguages[0]);
      } else {
        setContext(value[selectedLanguages[0]] || "");
        setTargetLanguage(selectedLanguages[0]);
      }
    }
  }, [selectedLanguages, value, id, isOpen]);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [responses, setResponses] = useState<GenerateResponse[]>([]);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [editedResponses, setEditedResponses] = useState<
    Record<number, string>
  >({});
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const currentResponse = responses[currentResponseIndex];
  const hasResponses = responses.length > 0;

  const LANGUAGE_OPTIONS = supportedLanguages
    .filter((language) => selectedLanguages.includes(language.value))
    .map((language) => ({
      label: language.label,
      value: language.value,
    }));

  const {
    data = { tone: [], placement: [] },
    isLoading: isLoadingToneOptions,
  } = useQuery({
    queryKey: ["tone-options"],
    queryFn: () => GET<LanguageMetadata>(`/api/languages/metadata`),
  });

  const TONE_OPTIONS = useMemo(() => {
    if (isLoadingToneOptions) return [];
    return data?.tone?.map((tone: Tone) => ({
      label: tone.label,
      value: tone.slug,
    }));
  }, [data, isLoadingToneOptions]);

  const resetModal = useCallback(() => {
    setContext("");
    setTone(getSavedTone());
    setTargetLanguage("bg");
    setSpecificInstructions("");
    setResponses([]);
    setCurrentResponseIndex(0);
    setIsGenerating(false);
    setEditedResponses({});
  }, []);

  const handleClose = useCallback(() => {
    // Cancel any ongoing request
    if (abortController) {
      abortController.abort();
    }
    resetModal();
    onClose();
  }, [resetModal, onClose, abortController]);

  const handleGenerate = async () => {
    if (!context?.trim()) return;

    setIsGenerating(true);
    try {
      const generateRequest: GenerateRequest = {
        rewrite: [
          {
            id: id,
            language: targetLanguage,
            text: context?.trim(),
            tone,
            context: context?.trim(),
            instructions: showInstructions
              ? specificInstructions.trim()
              : undefined,
            placementId,
          },
        ],
      };

      // Cancel any existing request
      if (abortController) {
        abortController.abort();
      }

      // Create new abort controller
      const controller = new AbortController();
      setAbortController(controller);

      const response = await POST<GenerateResponse[]>(
        "/api/languages/rewrite-with-context",
        generateRequest,
        false, // addUTMParams
        false, // logoutOnFail
        false, // suppressToastErrors
        controller.signal // AbortSignal
      );

      if (response && Array.isArray(response) && response.length > 0) {
        const newResponses = [...responses, ...response];
        setResponses(newResponses);
        setCurrentResponseIndex(newResponses.length - 1);
      }
    } catch (error) {
      // Handle AbortError differently
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Request was cancelled");
        return; // Don't show error toast for cancelled requests
      }
      console.error("Generation failed:", error);
      // TODO: Show error toast
    } finally {
      setIsGenerating(false);
      setAbortController(null);
    }
  };

  const handleKeep = () => {
    if (currentResponse) {
      const textToKeep =
        editedResponses[currentResponseIndex] || currentResponse.outputText;
      onKeep(textToKeep, currentResponse.language);
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentResponseIndex > 0) {
      setCurrentResponseIndex(currentResponseIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentResponseIndex < responses.length - 1) {
      setCurrentResponseIndex(currentResponseIndex + 1);
    }
  };

  const getGenerateButtonText = () => {
    if (isGenerating) return t("generate.modal.generating_cta");
    if (hasResponses) return t("generate.modal.generate_again_cta");
    return t("generate.modal.generate_cta");
  };

  return (
    <CustomModal
      open={isOpen}
      onClose={handleClose}
      title={t("generate.modal.title", { title: label })}
      size="large"
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
      noScroll
    >
      <CustomModal.Section>
        <BlockStack gap="400">
          <TextField
            label={t("generate.form.context.label")}
            value={context}
            onChange={setContext}
            multiline={4}
            maxHeight="150px"
            autoComplete="off"
          />

          {showInstructions && (
            <TextField
              label={t("generate.form.instructions.label")}
              value={specificInstructions}
              onChange={setSpecificInstructions}
              multiline={3}
              maxHeight="150px"
              autoComplete="off"
            />
          )}

          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="400">
              <Box minWidth="200px">
                <Select
                  label={t("generate.form.tone.label")}
                  options={TONE_OPTIONS}
                  value={tone}
                  onChange={handleToneChange}
                />
              </Box>

              {supportedLanguages.length > 1 && (
                <Box minWidth="200px">
                  <Select
                    label={t("generate.form.target_language.label")}
                    options={LANGUAGE_OPTIONS}
                    value={targetLanguage}
                    onChange={(locale: string) => {
                      setTargetLanguage(locale);
                      setContext(value[locale] ?? context);
                    }}
                  />
                </Box>
              )}
            </InlineStack>
            <Box>
              <Button
                variant="tertiary"
                onClick={() => setShowInstructions(!showInstructions)}
                pressed={showInstructions}
                icon={AdjustIcon}
              ></Button>
            </Box>
          </InlineStack>
        </BlockStack>
        <Box padding="200"></Box>
        <BlockStack gap="400">
          {/* Response Navigation Header */}

          {/* Generated Response */}
          {currentResponse && (
            <TextField
              label={""}
              value={
                editedResponses[currentResponseIndex] ||
                currentResponse.outputText
              }
              onChange={(value) =>
                setEditedResponses((prev) => ({
                  ...prev,
                  [currentResponseIndex]: value,
                }))
              }
              multiline={6}
              maxHeight="200px"
              autoComplete="off"
            />
          )}
        </BlockStack>
      </CustomModal.Section>

      <CustomModal.Section>
        <InlineStack align="space-between">
          {responses.length > 1 ? (
            <InlineStack align="space-between" blockAlign="center" gap="300">
              <Button
                icon={ChevronLeftIcon}
                variant="tertiary"
                onClick={handlePrevious}
                disabled={currentResponseIndex === 0}
              />
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("generate.response.navigation", {
                  current: currentResponseIndex + 1,
                  total: responses.length,
                })}
              </Text>
              <Button
                icon={ChevronRightIcon}
                variant="tertiary"
                onClick={handleNext}
                disabled={currentResponseIndex === responses.length - 1}
              />
            </InlineStack>
          ) : (
            <Box opacity="0"></Box>
          )}
          <InlineStack align="end" gap="300">
            <Button
              variant={hasResponses ? "secondary" : "primary"}
              onClick={handleGenerate}
              loading={isGenerating}
              disabled={!context?.trim() || isGenerating}
            >
              {getGenerateButtonText()}
            </Button>

            {hasResponses && (
              <Button
                variant="primary"
                onClick={handleKeep}
                disabled={!currentResponse}
              >
                {t("generate.modal.keep_cta")}
              </Button>
            )}
          </InlineStack>
        </InlineStack>
      </CustomModal.Section>
    </CustomModal>
  );
}
